// src/router/router.js

import { createRouter, createWebHistory } from 'vue-router'
import ROUTES from '@generio-ai/library-legacy/constants/routes'
import vuex from '@generio-ai/library-legacy/plugins/vuex'
import ViewStart from '@/views/ViewStart.vue'
import ViewAgent from '@/views/ViewAgent.vue'
import ViewCreate from '@/views/ViewCreate.vue'
import ViewModify from '@/views/ViewModify.vue'
import ViewLibrary from '@/views/ViewLibrary.vue'
import ViewLibraryFlows from '@/views/library/ViewLibraryFlows.vue'
import ViewLibraryModels from '@/views/library/ViewLibraryModels.vue'
import ViewLibraryImages from '@/views/library/ViewLibraryImages.vue'
import ViewMarket from '@/views/ViewMarket.vue'
import ViewMarketRoot from '@/views/market/ViewMarketRoot.vue'
import ViewMarketItem from '@/views/market/ViewMarketItem.vue'
import ViewTutorials from '@/views/ViewTutorials.vue'
import ViewCommunity from '@/views/ViewCommunity.vue'
import ViewSurvey from '@/views/ViewSurvey.vue'

const routes = [

	// redirects

	{
		path: ROUTES.APPSUITE.ROOT.path,
		name: ROUTES.APPSUITE.ROOT.name,
		redirect: ROUTES.APPSUITE.START.path
	},
	{
		path: ROUTES.APPSUITE.WELCOME.path,
		name: ROUTES.APPSUITE.WELCOME.name,
		redirect: () => ({
			path: ROUTES.APPSUITE.CREATE.path,
			query: { welcome: 'true' }
		})
	},
	{
		path: '/sketch',
		name: 'Redirect',
		redirect: ROUTES.APPSUITE.CREATE.path
	},
	{
		path: '/sketch++',
		name: 'Redirect',
		redirect: ROUTES.APPSUITE.CREATE.path
	},
	{
		path: '/sketch2',
		name: 'Redirect',
		redirect: ROUTES.APPSUITE.CREATE.path
	},

	// main routes

	{
		path: ROUTES.APPSUITE.START.path,
		name: ROUTES.APPSUITE.START.name,
		component: ViewStart
	},
	{
		path: ROUTES.APPSUITE.AGENT.path,
		name: ROUTES.APPSUITE.AGENT.name,
		component: ViewAgent
	},
	{
		path: ROUTES.APPSUITE.CREATE.path,
		name: ROUTES.APPSUITE.CREATE.name,
		component: ViewCreate
	},
	{
		path: ROUTES.APPSUITE.MODIFY.path,
		name: ROUTES.APPSUITE.MODIFY.name,
		component: ViewModify
	},
	{
		path: ROUTES.APPSUITE.LIBRARY.path,
		component: ViewLibrary,
		children: [
			{
				path: '',
				redirect: () => {
					return libraryState.lastNonAssetRoute || ROUTES.APPSUITE.LIBRARY_FLOWS.path
				}
			},
			{
				path: ROUTES.APPSUITE.LIBRARY_FLOWS.path,
				name: `${ROUTES.APPSUITE.LIBRARY.name} | ${ROUTES.APPSUITE.LIBRARY_FLOWS.name}`,
				component: ViewLibraryFlows,
			},
			{
				path: ROUTES.APPSUITE.LIBRARY_MODELS.path,
				name: `${ROUTES.APPSUITE.LIBRARY.name} | ${ROUTES.APPSUITE.LIBRARY_MODELS.name}`,
				component: ViewLibraryModels,
			},
			{
				path: ROUTES.APPSUITE.LIBRARY_IMAGES.path,
				name: `${ROUTES.APPSUITE.LIBRARY.name} | ${ROUTES.APPSUITE.LIBRARY_IMAGES.name}`,
				component: ViewLibraryImages,
			}
		]
	},
	{
		path: ROUTES.APPSUITE.MARKET.path,
		component: ViewMarket,
		children: [
			{
				path: ROUTES.APPSUITE.MARKET.path,
				name: ROUTES.APPSUITE.MARKET.name,
				component: ViewMarketRoot,
			},
			{
				path: ROUTES.APPSUITE.MARKET_ASSET.path,
				name: ROUTES.APPSUITE.MARKET_ASSET.name,
				component: ViewMarketItem,
				props: true,
			}
		]
	},
	{
		path: ROUTES.APPSUITE.TUTORIALS.path,
		name: ROUTES.APPSUITE.TUTORIALS.name,
		component: ViewTutorials
	},
	{
		path: ROUTES.APPSUITE.COMMUNITY.path,
		name: ROUTES.APPSUITE.COMMUNITY.name,
		component: ViewCommunity
	},
	{
		path: ROUTES.APPSUITE.SURVEY.path,
		name: ROUTES.APPSUITE.SURVEY.name,
		component: ViewSurvey
	},

	// catch-all redirect to homepage

	{ 
		path: '/:pathMatch(.*)*', 
		redirect: '/' 
	}
]

const router = createRouter({
	history: createWebHistory(),
	routes,
	scrollBehavior(to, from, savedPosition) {
		return savedPosition || { top: 0 }
	}
})

const libraryState = {
	lastNonAssetRoute: ROUTES.APPSUITE.LIBRARY_FLOWS.path
}

router.beforeEach((to, from, next) => {

	// track last visited library routes
	if (to.path.startsWith(ROUTES.APPSUITE.LIBRARY.path)) {
		if (!to.path.startsWith(ROUTES.APPSUITE.LIBRARY.path + '/asset/')) {
			libraryState.lastNonAssetRoute = to.path
		}
	}

	// loading a different view
	if (to.path !== from.path) {
		vuex.dispatch('overlay/show')
	}

	// set title of page
    document.title = to.name ? 'GENERIO | ' + to.name : 'GENERIO | We make 3D modeling child\'s play'

	// check if user is authenticated
	const isAuthenticated = vuex.getters['auth/isAuthenticated']
	if (!isAuthenticated) {
		vuex.dispatch('router/openRoute', { route: ROUTES.AUTHENTICATION.ROOT })
		return
	}

	// check for app permissions
	const hasAppAgent = vuex.getters['auth/hasAppAgent']
	const hasAppCreate = vuex.getters['auth/hasAppCreate']
	const hasAppModify = vuex.getters['auth/hasAppModify']
	const hasAppLibrary = vuex.getters['auth/hasAppLibrary']
	const hasAppMarket = vuex.getters['auth/hasAppMarket']
	const hasAppTutorials = vuex.getters['auth/hasAppTutorials']
	const hasAppCommunity = vuex.getters['auth/hasAppCommunity']
	if ((to.path === ROUTES.APPSUITE.AGENT.path && !hasAppAgent) ||
		(to.path === ROUTES.APPSUITE.CREATE.path && !hasAppCreate) ||
		(to.path === ROUTES.APPSUITE.MODIFY.path && !hasAppModify) ||
		(to.path === ROUTES.APPSUITE.LIBRARY.path && !hasAppLibrary) ||
		(to.path === ROUTES.APPSUITE.MARKET.path && !hasAppMarket) ||
		(to.path === ROUTES.APPSUITE.TUTORIALS.path && !hasAppTutorials) ||
		(to.path === ROUTES.APPSUITE.COMMUNITY.path && !hasAppCommunity))
	{
		next({ path: ROUTES.APPSUITE.ROOT.path })	
		return 
	}

	next()
})

// ensure overlay is hidden after each route change
router.afterEach(async (to, from, failure) => {
	//await nextTick()
	vuex.dispatch('overlay/hide')
})
router.onError(() => vuex.dispatch('overlay/hide'))
window.addEventListener('pagehide', () => vuex.dispatch('overlay/hide'))

export default router
export { libraryState }